/**
 * Per-chain minimum order size, learned from what the provider actually did.
 *
 * No provider publishes one number that stays true: the floor moves with
 * network fees, and it differs per chain for the same coin. So we observe it.
 * A refused invoice teaches "this amount was too small"; an accepted one
 * teaches "this amount was enough".
 *
 * The two observations bracket the real minimum in a half-open interval
 * (refused, accepted]. This module turns that bracket into the single number
 * the storefront and the checkout endpoint both use, so the card that hides a
 * coin and the endpoint that refuses it cannot disagree.
 */
/**
 * Whether a coin may be offered for an order of this size.
 *
 * ONE rule for the three places a coin is chosen -- the plan form, the payment
 * screen's switcher, the refill form -- and for the endpoint that refuses one,
 * so a coin the customer can press is a coin the worker will open.
 */
export function coinFits(minCents, totalCents) {
    return minCents === null || totalCents >= minCents;
}
/**
 * The smallest amount worth attempting on this chain, or null for "no opinion".
 *
 * The HIGHER of the operator's floor and the learned one. The operator's is a
 * business floor and nothing learned may lower it; but a provider refusing
 * above it is still a refusal, and offering the coin under that would send
 * every such order to the same `below_min`.
 */
export function attemptFloorCents(o) {
    const learned = learnedFloorCents(o);
    if (o.operatorCents === null)
        return learned;
    return learned === null ? o.operatorCents : Math.max(o.operatorCents, learned);
}
/**
 * What the provider's own answers say, or null for "no opinion".
 *
 * Null when nothing has been refused. That is deliberate: with no refusal on
 * record, every number we could return would be a guess, and a guessed floor
 * blocks a customer who would have been served. Letting the provider refuse
 * costs one round trip and teaches us the real figure.
 */
function learnedFloorCents(o) {
    const refused = o.refusedCents;
    if (refused === null)
        return null;
    const accepted = o.acceptedCents;
    // The normal case: the two observations bracket the minimum, and one cent
    // above the largest refusal is the tightest bound we can justify.
    if (accepted === null || accepted > refused)
        return refused + 1;
    // Contradiction: an amount was accepted that is not larger than one refused.
    // Both were true when they were recorded, so the provider's minimum moved.
    // The newer observation is the one that describes today.
    const acceptedNewer = (o.acceptedAt ?? "") > (o.refusedAt ?? "");
    return acceptedNewer ? accepted : refused + 1;
}
/** The largest operator floor the admin accepts, USD cents: $500. */
export const OPERATOR_FLOOR_MAX_CENTS = 50_000;
//# sourceMappingURL=floors.js.map